import React, { useState } from "react";
import "./DeleteHighlightConfirm.css";

const DeleteHighlightConfirm = ({
  onCancel,
  onConfirm,
  message,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  workingLabel = "Deleting...",
  workingStatusLabel = "Deleting...",
  confirmDisabled = false,
  selectionLabel = "",
  selectionHint = "",
  selectionOptions = [],
  selectedOptionIds = [],
  onToggleOption,
  onSelectAll,
  onClearSelection,
}) => {
  const [isWorking, setIsWorking] = useState(false);
  const hasSelectionOptions = Array.isArray(selectionOptions) && selectionOptions.length > 0;
  const selectedCount = Array.isArray(selectedOptionIds) ? selectedOptionIds.length : 0;
  const isConfirmDisabled = isWorking || confirmDisabled;

  const handleConfirm = async () => {
    if (isConfirmDisabled) return;

    setIsWorking(true);

    try {
      await Promise.resolve(onConfirm?.());
    } catch (err) {
      console.error("DeleteHighlightConfirm onConfirm failed:", err);
      setIsWorking(false);
    }
  };

  const handleCancel = () => {
    if (isWorking) return;
    onCancel?.();
  };

  return (
    <>
      <div
        className={`DEL-Highlight-Confirm-overlay ${isWorking ? "is-working" : ""}`}
        onClick={handleCancel}
        aria-hidden="true"
      />

      <div
        className="DEL-Highlight-Confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-busy={isWorking ? "true" : "false"}
      >
        <p className="non-blur">{message}</p>

        {hasSelectionOptions ? (
          <div className="DEL-Highlight-Confirm-selection non-blur">
            <div className="DEL-Highlight-Confirm-selection__header">
              <span className="DEL-Highlight-Confirm-selection__title">
                {selectionLabel || "Select items to delete"}
              </span>
              {selectionOptions.length > 1 ? (
                <div className="DEL-Highlight-Confirm-selection__bulk">
                  <button
                    type="button"
                    className="DEL-Highlight-Confirm-inline-btn"
                    onClick={() => !isWorking && onSelectAll?.()}
                    disabled={isWorking}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="DEL-Highlight-Confirm-inline-btn"
                    onClick={() => !isWorking && onClearSelection?.()}
                    disabled={isWorking}
                  >
                    Clear
                  </button>
                </div>
              ) : null}
            </div>

            {selectionHint ? (
              <p className="DEL-Highlight-Confirm-selection__hint">{selectionHint}</p>
            ) : null}

            <div className="DEL-Highlight-Confirm-selection__list">
              {selectionOptions.map((option) => {
                const isSelected = selectedOptionIds.includes(option.id);
                return (
                  <label
                    key={option.id}
                    className={`DEL-Highlight-Confirm-selection__item ${
                      isSelected ? "is-selected" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => !isWorking && onToggleOption?.(option.id)}
                      disabled={isWorking}
                    />
                    <span className="DEL-Highlight-Confirm-selection__copy">
                      <strong>{option.label}</strong>
                      {option.description ? (
                        <span>{option.description}</span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="DEL-Highlight-Confirm-selection__summary">
              {selectedCount} selected
            </div>
          </div>
        ) : null}

        {isWorking ? (
          <div className="DEL-Highlight-Confirm-status non-blur">
            <span className="DEL-Highlight-Confirm-spinner" aria-hidden="true" />
            <span>{workingStatusLabel}</span>
          </div>
        ) : null}

        <div className="DEL-Highlight-Confirm-actions">
          <button
            className={`DEL-Highlight-Confirm-btn DEL-Highlight-Confirm-btn--delete non-blur ${
              isConfirmDisabled ? "is-disabled" : ""
            }`}
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
          >
            {isWorking ? workingLabel : confirmLabel}
          </button>

          <button
            className={`app-cancel-control DEL-Highlight-Confirm-btn DEL-Highlight-Confirm-btn--cancel non-blur ${
              isWorking ? "is-disabled" : ""
            }`}
            onClick={handleCancel}
            disabled={isWorking}
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </>
  );
};

export default DeleteHighlightConfirm;
